/**
 * Team Approvals Widget — dashboard super-admin-only panel that shows
 * pending team-member applications with one-click approve / reject.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, CheckCircle2, XCircle, ChevronRight, Loader2, Mail, Phone, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeamApprovalsWidget() {
  const [pending, setPending] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [selectedRole, setSelectedRole] = useState({}); // { user_id: role_id }

  const load = async () => {
    try {
      const token = localStorage.getItem("sterilemate_token");
      if (!token) { setLoading(false); return; }
      const res = await fetch(`${API}/api/admin/team-pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setLoading(false); return; }
      const data = await res.json();
      setPending(data.pending || []);
      setRoles(data.roles || []);
      // Default each row's approve-role to what they requested.
      const dr = {};
      (data.pending || []).forEach(p => { dr[p.id] = p.requested_role || "team_member"; });
      setSelectedRole(dr);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, []);

  const approve = async (u) => {
    setProcessingId(u.id);
    try {
      const token = localStorage.getItem("sterilemate_token");
      const res = await fetch(`${API}/api/admin/team-pending/${u.id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role: selectedRole[u.id] || u.requested_role || "team_member" }),
      });
      if (!res.ok) throw new Error("Approval failed");
      toast.success(`${u.name} approved!`);
      setPending(prev => prev.filter(p => p.id !== u.id));
    } catch (e) {
      toast.error("Approval failed. Please try again.");
    } finally {
      setProcessingId(null);
    }
  };

  const reject = async (u) => {
    if (!window.confirm(`Reject ${u.name}'s application?`)) return;
    setProcessingId(u.id);
    try {
      const token = localStorage.getItem("sterilemate_token");
      const res = await fetch(`${API}/api/admin/team-pending/${u.id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: null }),
      });
      if (!res.ok) throw new Error("Reject failed");
      toast.success("Application rejected");
      setPending(prev => prev.filter(p => p.id !== u.id));
    } catch (e) {
      toast.error("Reject failed. Please try again.");
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="team-approvals-widget-loading">
        <div className="animate-pulse">
          <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
          <div className="h-20 bg-slate-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (pending.length === 0) return null; // hide when no work to do

  return (
    <div
      className="bg-white rounded-xl border border-amber-300 shadow-lg overflow-hidden"
      data-testid="team-approvals-widget"
    >
      {/* Solid amber header (email-safe pattern, but here for visual urgency) */}
      <div className="bg-amber-500 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/25 rounded-lg flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white leading-tight">
                {pending.length} team {pending.length === 1 ? "member" : "members"} waiting for your approval
              </h3>
              <p className="text-amber-100 text-sm">
                Review each application and pick their final role before activating access.
              </p>
            </div>
          </div>
          <Link
            to="/admin/team-approvals"
            className="hidden md:inline-flex items-center gap-1 text-sm font-bold text-white hover:underline"
            data-testid="team-approvals-full-page-link"
          >
            Full page <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Pending list */}
      <div className="p-4 max-h-[520px] overflow-y-auto space-y-3">
        {pending.map((u) => (
          <div
            key={u.id}
            className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:border-amber-300 transition-colors"
            data-testid={`team-approval-row-${u.id}`}
          >
            <div className="flex items-start justify-between mb-3 gap-3">
              <div className="min-w-0">
                <h4 className="font-bold text-slate-900 truncate">{u.name || "—"}</h4>
                <p className="text-sm text-slate-600 truncate flex items-center gap-1.5"><Mail className="w-3 h-3" /> {u.email}</p>
                {u.phone && (
                  <p className="text-sm text-slate-500 truncate flex items-center gap-1.5"><Phone className="w-3 h-3" /> {u.phone}</p>
                )}
              </div>
              <span className="px-2 py-1 bg-slate-900 text-white rounded text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">
                Requested: {u.requested_role_label || u.requested_role}
              </span>
            </div>

            {u.linkedin_url && (
              <p className="text-xs text-slate-500 mb-3">
                LinkedIn: <a href={u.linkedin_url} target="_blank" rel="noreferrer" className="text-teal-700 hover:underline">{u.linkedin_url}</a>
              </p>
            )}

            {/* Role picker + actions */}
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <div className="flex-1 min-w-0">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-1">
                  Assign role on approval
                </label>
                <select
                  value={selectedRole[u.id] || u.requested_role || "team_member"}
                  onChange={(e) => setSelectedRole(s => ({ ...s, [u.id]: e.target.value }))}
                  className="w-full h-9 px-2 border border-slate-300 rounded text-sm bg-white"
                  data-testid={`team-approval-role-select-${u.id}`}
                >
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 items-end">
                <Button
                  size="sm"
                  onClick={() => approve(u)}
                  disabled={processingId === u.id}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                  data-testid={`team-approval-approve-${u.id}`}
                >
                  {processingId === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => reject(u)}
                  disabled={processingId === u.id}
                  className="text-slate-600 border-slate-300"
                  data-testid={`team-approval-reject-${u.id}`}
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-slate-50 px-4 py-3 border-t border-slate-200 flex items-center justify-between">
        <p className="text-xs text-slate-500 flex items-center gap-1.5">
          <Users className="w-3 h-3" /> Team invitations skip this queue automatically.
        </p>
        <Link
          to="/admin/team-approvals"
          className="text-xs font-bold text-teal-700 hover:text-teal-900 inline-flex items-center gap-1"
        >
          Manage invites & rejections <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
